// @oasis-permission-inherits: /ai-edits (studio-edits:view)
import { Link, createFileRoute } from "@tanstack/react-router";
import { CopyIcon, FileJson } from "lucide-react";
import { useMemo } from "react";
import { toast } from "sonner";
import { P, match } from "ts-pattern";
import { z } from "zod";

import { Badge } from "~/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { Skeleton } from "~/components/ui/skeleton";
import { cn } from "~/lib/utils";
import { ShadowOperationIdBadge } from "~/modules/agave-shadow-renders/components/shadow-operation-id-badge";
import {
  CallerBadge,
  CreditLabel,
  ModeBadge,
  ProductTypeBadge,
  agaveShadowRenderCallerSearchSchema,
  agaveShadowRenderModeSearchSchema,
  agaveShadowRenderProductTypeSearchSchema,
} from "~/modules/agave-shadow-renders/components/shadow-render-fields";
import { createAgaveShadowProjectPlayback } from "~/modules/agave-shadow-renders/project-playback";
import { getAgaveShadowRenderQuery } from "~/modules/agave-shadow-renders/queries";
import {
  type AgaveShadowRenderCredits,
  type AgaveShadowRenderSummary,
  agaveShadowRenderStatusSchema,
} from "~/modules/agave-shadow-renders/schemas";
import { JerboaProjectPreview } from "~/modules/projects/components/jerboa-project-preview";
import { getProjectDetailQuery, getProjectSnapshotPlaybackQuery } from "~/modules/projects/queries";
import type { ProjectSnapshotPlayback } from "~/modules/projects/schemas";
import { formatRelativeTime } from "~/modules/videos/utils";

const searchSchema = z.object({
  caller: agaveShadowRenderCallerSearchSchema.optional().default("all").catch("all"),
  mode: agaveShadowRenderModeSearchSchema.optional().default("all").catch("all"),
  page: z.coerce.number().int().positive().optional().default(1).catch(1),
  productType: agaveShadowRenderProductTypeSearchSchema.optional().default("all").catch("all"),
  status: agaveShadowRenderStatusSchema.optional().default("complete").catch("complete"),
});

type JsonRecord = Record<string, unknown>;
type AgaveShadowDetailSearch = z.infer<typeof searchSchema>;

const creditMetricFormatter = new Intl.NumberFormat("en-US", {
  maximumFractionDigits: 4,
});

export const Route = createFileRoute("/_dash/ai-edits/agave-shadow-renders/$shadowRenderId")({
  validateSearch: searchSchema,
  component: RouteComponent,
  async loader({ params, context }) {
    const { queryClient } = context;
    await queryClient.prefetchQuery(
      getAgaveShadowRenderQuery.queryOptions({
        pathParams: { shadowRenderId: params.shadowRenderId },
      })
    );
  },
  pendingComponent: PendingComponent,
});

function PendingComponent() {
  const search = Route.useSearch();
  return (
    <div className="space-y-6">
      <BackLink search={search} />
      <Skeleton className="h-8 w-80 rounded-md" />
      <div className="grid gap-6 xl:grid-cols-[minmax(0,720px)_minmax(480px,1fr)]">
        <div className="grid max-w-[640px] gap-3 sm:grid-cols-2">
          <Skeleton className="min-h-[300px] rounded-md" />
          <Skeleton className="min-h-[300px] rounded-md" />
        </div>
        <Skeleton className="h-96 rounded-lg" />
      </div>
    </div>
  );
}

function BackLink({ search }: { search: AgaveShadowDetailSearch }) {
  return (
    <Link
      to="/ai-edits/agave-shadows"
      search={search}
      className="text-sm text-muted-foreground hover:underline"
    >
      &larr; Back to agave shadow
    </Link>
  );
}

function RouteComponent() {
  const { shadowRenderId } = Route.useParams();
  const search = Route.useSearch();
  const { data } = getAgaveShadowRenderQuery.useSuspenseQuery({
    pathParams: { shadowRenderId },
  });

  const result = data.result;
  const sourceProjectId = result.sourceProjectId;
  const operation = data.operation;
  const credits = data.credits;
  const session = recordValue(operation.session);
  const turns = Array.isArray(session?.turns) ? session.turns : [];
  const sessionState = recordValue(session?.state);
  const originalMeteredCredits = credits?.originalMeteredCredits ?? result.originalMeteredCredits;
  const agaveMeteredCredits = credits?.agaveMeteredCredits ?? result.agaveMeteredCredits;

  return (
    <div className="space-y-6">
      <BackLink search={search} />

      <div>
        <h1 className="text-xl font-bold">agave shadow</h1>
        <p className="text-sm text-muted-foreground">
          {formatRelativeTime(result.createdAt)} by {result.userId ?? "unknown user"}
        </p>
        {sourceProjectId && (
          <button
            type="button"
            className="mt-2 flex max-w-full items-center gap-1.5 rounded bg-muted px-2 py-1 text-left text-xs text-muted-foreground hover:bg-muted/80"
            onClick={() => copyToClipboard(sourceProjectId, "Source project ID copied")}
          >
            <span className="shrink-0 font-medium">Source project ID</span>
            <span className="min-w-0 break-all font-mono">{sourceProjectId}</span>
            <CopyIcon className="size-3 shrink-0 opacity-60" aria-hidden="true" />
          </button>
        )}
      </div>

      <div className="grid gap-6 xl:grid-cols-[minmax(0,720px)_minmax(480px,1fr)]">
        <div className="min-w-0 space-y-4">
          <ProjectComparison result={result} />
          <div className="space-y-2 rounded-md border bg-card p-2">
            <div className="grid grid-cols-2 gap-2">
              <div className="min-w-0">
                <CreditLabel credits={originalMeteredCredits} />
              </div>
              <div className="flex min-w-0 justify-end">
                <CreditLabel credits={agaveMeteredCredits} />
              </div>
            </div>
            <div className="flex min-w-0 flex-wrap items-center gap-1.5">
              <ModeBadge mode={result.mode} />
              <CallerBadge caller={result.caller} />
              <ProductTypeBadge productType={result.productType} />
              <ShadowOperationIdBadge value={result.agaveOperationId} className="flex-1 basis-40" />
            </div>
          </div>
          {result.error && (
            <CopyableTextBlock
              value={result.error}
              copyLabel="Render Error"
              contentClassName="border border-destructive bg-destructive/10 text-sm text-destructive"
            />
          )}
        </div>

        <div className="min-w-0 space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Run Details</CardTitle>
            </CardHeader>
            <CardContent>
              <DetailGrid result={result} operation={operation} />
            </CardContent>
          </Card>
          <MeteredCreditsCard result={result} credits={credits} />
        </div>
      </div>

      <AgentTurnsCard turns={turns} />

      {sessionState && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Session State</CardTitle>
          </CardHeader>
          <CardContent>
            <JsonBlock value={sessionState} copyLabel="Session State" />
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function ProjectComparison({ result }: { result: AgaveShadowRenderSummary }) {
  const directSourcePlayback = useMemo(
    () =>
      createAgaveShadowProjectPlayback(result, {
        label: "Original project",
        snapshotId: "source-project",
        uri: result.sourceProjectProtoUri,
        dataUrl: result.sourceProjectProtoUrl,
        assetUrlMap: result.sourceProjectAssetUrlMap,
      }),
    [result]
  );
  const shadowPlayback = useMemo(
    () =>
      createAgaveShadowProjectPlayback(result, {
        label: "Shadow project",
        snapshotId: "shadow-project",
        uri: result.projectDataUri,
        dataUrl: result.projectDataUrl,
        assetUrlMap: result.projectAssetUrlMap,
      }),
    [result]
  );
  const shouldLoadSourceProjectPlayback =
    !directSourcePlayback && result.sourceCaptionsProjectId != null;
  const {
    data: sourceProjectData,
    isLoading: isSourceProjectLoading,
    isFetching: isSourceProjectFetching,
  } = getProjectDetailQuery.useQuery(
    {
      pathParams: { projectId: result.sourceCaptionsProjectId ?? "" },
    },
    {
      enabled: shouldLoadSourceProjectPlayback,
    }
  );
  const sourceProject = sourceProjectData?.project ?? null;
  const sourceSnapshot = useMemo(() => {
    if (!sourceProject) {
      return null;
    }

    const sourceSnapshotId =
      sourceProject.currentSnapshotId ?? sourceProject.snapshots.at(-1)?.id ?? null;
    return sourceProject.snapshots.find((snapshot) => snapshot.id === sourceSnapshotId) ?? null;
  }, [sourceProject]);
  const {
    data: sourceSnapshotPlaybackData,
    isLoading: isSourceSnapshotPlaybackLoading,
    isFetching: isSourceSnapshotPlaybackFetching,
    error: sourceSnapshotPlaybackError,
  } = getProjectSnapshotPlaybackQuery.useQuery(
    {
      pathParams: {
        projectId: result.sourceCaptionsProjectId ?? "",
        snapshotId: sourceSnapshot?.id ?? "current",
      },
      params: {
        syncToken: sourceSnapshot?.syncToken ?? "",
        ownerUserId: sourceProject?.createdBy ?? "",
      },
    },
    {
      enabled: Boolean(
        shouldLoadSourceProjectPlayback &&
        sourceProject?.hasDataModel &&
        sourceProject.createdBy &&
        sourceSnapshot
      ),
    }
  );
  const sourcePlayback = directSourcePlayback ?? sourceSnapshotPlaybackData?.playback ?? null;
  const sourcePlaybackError = sourcePlayback ? null : sourceSnapshotPlaybackError;
  const sourceEmptyDescription = getSourceProjectEmptyDescription(result);
  const isSourcePlaybackLoading =
    shouldLoadSourceProjectPlayback &&
    (isSourceProjectLoading ||
      isSourceProjectFetching ||
      isSourceSnapshotPlaybackLoading ||
      isSourceSnapshotPlaybackFetching);

  return (
    <div className="grid max-w-[640px] gap-3 sm:grid-cols-2">
      <ProjectPlayerPanel
        title="Original project"
        emptyTitle="Original project unavailable"
        emptyDescription={sourceEmptyDescription}
        uri={result.sourceProjectProtoUri}
        playback={sourcePlayback}
        isLoading={isSourcePlaybackLoading}
        playbackError={sourcePlaybackError}
        previewFrameClassName="max-w-[300px]"
        showUriFallback={false}
      />
      <ProjectPlayerPanel
        title="Shadow project"
        emptyTitle="Shadow project unavailable"
        emptyDescription="No shadow project data URI was found."
        uri={result.projectDataUri}
        playback={shadowPlayback}
        previewFrameClassName="max-w-[300px]"
      />
    </div>
  );
}

function getSourceProjectEmptyDescription(result: AgaveShadowRenderSummary) {
  if (
    result.sourceProjectProtoUri &&
    !result.sourceProjectProtoUrl &&
    !result.sourceCaptionsProjectId
  ) {
    return "Source proto URI was recorded, but Oasis needs a signed proto URL or source captions project ID to render it.";
  }
  if (!result.sourceProjectProtoUri && !result.sourceCaptionsProjectId) {
    return "No source project proto URI or source captions project ID was found.";
  }
  if (result.sourceCaptionsProjectId) {
    return "Source captions project did not return playable snapshot data.";
  }
  return "No browser-fetchable source project data was found.";
}

function ProjectPlayerPanel({
  title,
  emptyTitle,
  emptyDescription,
  uri,
  playback,
  isLoading = false,
  playbackError = null,
  previewFrameClassName,
  showUriFallback = true,
}: {
  title: string;
  emptyTitle: string;
  emptyDescription: string;
  uri: string | null | undefined;
  playback: ProjectSnapshotPlayback | null;
  isLoading?: boolean;
  playbackError?: Error | null;
  previewFrameClassName?: string;
  showUriFallback?: boolean;
}) {
  const content = match({ isLoading, playback, playbackError, showUriFallback, uri })
    .with({ playback: P.not(P.nullish) }, ({ playback }) => (
      <JerboaProjectPreview
        playback={playback}
        isLoading={isLoading}
        playbackError={playbackError}
        frameClassName={previewFrameClassName}
      />
    ))
    .with({ isLoading: true }, () => <Skeleton className="min-h-[300px] rounded-md" />)
    .with({ playbackError: P.not(P.nullish) }, ({ playbackError }) => (
      <JerboaProjectPreview
        playback={null}
        isLoading={false}
        playbackError={playbackError}
        frameClassName={previewFrameClassName}
      />
    ))
    .with({ showUriFallback: true, uri: P.string }, ({ uri }) => (
      <ProjectUriPanel label={title} uri={uri} />
    ))
    .otherwise(() => <PlaybackPlaceholder title={emptyTitle} description={emptyDescription} />);

  return (
    <section className="min-w-0 space-y-2">
      <div className="flex min-h-8 items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2">
          <FileJson className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
          <h2 className="truncate text-sm font-semibold">{title}</h2>
        </div>
        {uri && (
          <button
            type="button"
            className="min-w-0 truncate rounded bg-muted px-2 py-1 font-mono text-[10px] text-muted-foreground hover:bg-muted/80"
            onClick={() => copyToClipboard(uri, `${title} URI copied`)}
          >
            {uri}
          </button>
        )}
      </div>

      {content}
    </section>
  );
}

function ProjectUriPanel({ label, uri }: { label: string; uri: string }) {
  return (
    <div className="flex min-h-[300px] flex-col justify-center rounded-md border bg-card p-6">
      <div className="mx-auto max-w-xl space-y-4 text-center">
        <FileJson className="mx-auto size-8 text-muted-foreground" aria-hidden="true" />
        <div>
          <h2 className="text-base font-semibold">{label}</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Project data was recorded, but this URI is not directly browser-fetchable.
          </p>
        </div>
        <button
          type="button"
          className="mx-auto flex max-w-full items-center gap-2 rounded bg-muted px-3 py-2 font-mono text-xs break-all hover:bg-muted/80"
          onClick={() => copyToClipboard(uri, `${label} URI copied`)}
        >
          <CopyIcon className="size-3.5 shrink-0" aria-hidden="true" />
          {uri}
        </button>
      </div>
    </div>
  );
}

function PlaybackPlaceholder({ title, description }: { title: string; description: string }) {
  return (
    <div className="flex min-h-[300px] items-center justify-center rounded-md border bg-muted/20 p-6 text-center">
      <div>
        <h2 className="text-base font-semibold">{title}</h2>
        <p className="mt-1 text-sm text-muted-foreground">{description}</p>
      </div>
    </div>
  );
}

function DetailGrid({
  result,
  operation,
}: {
  result: AgaveShadowRenderSummary;
  operation: JsonRecord;
}) {
  const rows: Array<[string, unknown]> = [
    ["Shadow render ID", result.id],
    ["Agave operation ID", result.agaveOperationId],
    ["Original operation ID", result.originalOperationId],
    ["Original charge group ID", result.originalChargeGroupId],
    ["Agave charge group ID", result.agaveChargeGroupId],
    ["Source workflow", result.sourceWorkflowName],
    ["Source workflow ID", result.sourceWorkflowId],
    ["Agave workflow ID", result.agaveWorkflowId],
    ["Agave workflow run ID", result.agaveWorkflowRunId],
    ["Product type", result.productType],
    ["Caller", result.caller],
    ["User ID", result.userId],
    ["Org ID", result.orgId],
    ["Mode", result.mode],
    ["Source video asset ID", result.sourceVideoAssetId],
    ["Source project ID", result.sourceProjectId],
    ["Source captions project ID", result.sourceCaptionsProjectId],
    ["Source project proto URI", result.sourceProjectProtoUri],
    ["Shadow video asset ID", result.videoAssetId],
    ["Captions project ID", result.captionsProjectId],
    ["Project proto URI", result.projectProtoUri],
    ["Agave project data URI", result.projectDataUri],
    ["Original credits charged", result.originalCreditsCharged],
    ["Original metered credits", result.originalMeteredCredits],
    ["Original charge count", result.originalChargeCount],
    ["Agave credits charged", result.agaveCreditsCharged],
    ["Agave metered credits", result.agaveMeteredCredits],
    ["Agave charge count", result.agaveChargeCount],
    ["Created", formatDate(result.createdAt)],
    ["Updated", result.updatedAt ? formatDate(result.updatedAt) : null],
  ];

  const request = recordValue(operation.request);
  const inputs = recordValue(request?.inputs);
  const goal = stringValue(inputs?.goal) ?? result.goal;

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-3">
        {rows.map(([label, value]) => (
          <DetailRow key={label} label={label} value={value} />
        ))}
      </div>

      {goal && (
        <div className="space-y-2">
          <div className="text-sm font-medium text-muted-foreground">Goal</div>
          <CopyableTextBlock
            value={goal}
            copyLabel="Goal"
            contentClassName="max-h-64 border bg-muted/30 text-sm leading-6"
          />
        </div>
      )}
    </div>
  );
}

function MeteredCreditsCard({
  result,
  credits,
}: {
  result: AgaveShadowRenderSummary;
  credits?: AgaveShadowRenderCredits | null;
}) {
  const rows = [
    {
      label: "Original",
      metered: credits?.originalMeteredCredits ?? result.originalMeteredCredits,
      charged: credits?.originalCreditsCharged ?? result.originalCreditsCharged,
      chargeCount: credits?.originalChargeCount ?? result.originalChargeCount,
    },
    {
      label: "Agave",
      metered: credits?.agaveMeteredCredits ?? result.agaveMeteredCredits,
      charged: credits?.agaveCreditsCharged ?? result.agaveCreditsCharged,
      chargeCount: credits?.agaveChargeCount ?? result.agaveChargeCount,
    },
  ];
  const hasCredits = rows.some((row) =>
    [row.metered, row.charged, row.chargeCount].some((value) => value != null)
  );

  if (!hasCredits) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Credits Metered</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid gap-3 sm:grid-cols-2">
          {rows.map((row) => (
            <div key={row.label} className="min-w-0 rounded-md border bg-muted/20 p-3">
              <div className="text-sm font-medium">{row.label}</div>
              <dl className="mt-3 grid grid-cols-[max-content_1fr] gap-x-3 gap-y-2 text-sm">
                <dt className="text-muted-foreground">Metered</dt>
                <dd className="min-w-0 text-right font-mono">
                  {formatCreditMetric(row.metered, "credits")}
                </dd>
                <dt className="text-muted-foreground">Charged</dt>
                <dd className="min-w-0 text-right font-mono">
                  {formatCreditMetric(row.charged, "credits")}
                </dd>
                <dt className="text-muted-foreground">Charges</dt>
                <dd className="min-w-0 text-right font-mono">
                  {formatCreditMetric(row.chargeCount)}
                </dd>
              </dl>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function DetailRow({ label, value }: { label: string; value: unknown }) {
  const text = value == null || value === "" ? null : String(value);
  let valueNode = <span className="text-sm text-muted-foreground">N/A</span>;
  if (text) {
    valueNode = <CopyableInlineValue value={text} copyLabel={label} />;
  }

  return (
    <>
      <p className="text-sm font-medium text-muted-foreground">{label}</p>
      {valueNode}
    </>
  );
}

function AgentTurnsCard({ turns }: { turns: unknown[] }) {
  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <CardTitle className="text-base">Agent Turns</CardTitle>
          <Badge variant="secondary">{turns.length} turns</Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {turns.length === 0 && (
          <p className="text-sm text-muted-foreground">No agent turns recorded.</p>
        )}
        {turns.map((turn, index) => (
          <AgentTurnCard key={index} turn={turn} />
        ))}
      </CardContent>
    </Card>
  );
}

function AgentTurnCard({ turn }: { turn: unknown }) {
  const turnRecord = recordValue(turn);
  const toolCall = recordValue(turnRecord?.toolCall);
  const turnState = recordValue(turnRecord?.state);
  const iteration = numberValue(turnRecord?.iteration);
  const thought = stringValue(turnRecord?.thought);
  const toolName = stringValue(toolCall?.name);
  const toolArgs = toolCall && "args" in toolCall ? toolCall.args : null;
  const output = stringValue(turnState?.output);
  const error = stringValue(turnState?.error);
  const status = stringValue(turnState?.status) ?? "unknown";

  return (
    <div className="space-y-3 rounded-lg border p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="outline">turn {iteration ?? "-"}</Badge>
          <StatusText status={status} />
          {toolName && <Badge variant="secondary">{toolName}</Badge>}
        </div>
      </div>
      {thought && (
        <div className="space-y-1">
          <div className="text-xs font-medium uppercase text-muted-foreground">Thought</div>
          <CopyableTextBlock
            value={thought}
            copyLabel="Thought"
            contentClassName="max-h-64 bg-transparent p-0 pr-10 text-sm leading-6"
          />
        </div>
      )}
      {toolArgs !== null && (
        <div className="space-y-1">
          <div className="text-xs font-medium uppercase text-muted-foreground">Tool Args</div>
          <JsonBlock value={toolArgs} copyLabel="Tool Args" />
        </div>
      )}
      {output && (
        <div className="space-y-1">
          <div className="text-xs font-medium uppercase text-muted-foreground">Output</div>
          <CopyableTextBlock
            value={output}
            copyLabel="Output"
            contentClassName="max-h-72 text-xs"
          />
        </div>
      )}
      {error && (
        <CopyableTextBlock
          value={error}
          copyLabel="Error"
          contentClassName="border border-destructive bg-destructive/10 text-sm text-destructive"
        />
      )}
    </div>
  );
}

function StatusText({ status }: { status: string }) {
  if (status === "complete") {
    return <Badge className="bg-green-600 text-white hover:bg-green-600">complete</Badge>;
  }
  if (status === "failed") {
    return <Badge variant="destructive">failed</Badge>;
  }
  return <Badge variant="secondary">{status}</Badge>;
}

function JsonBlock({ value, copyLabel = "JSON" }: { value: unknown; copyLabel?: string }) {
  const text = JSON.stringify(value, null, 2) ?? String(value);

  return <CopyableTextBlock value={text} copyLabel={copyLabel} />;
}

function CopyableInlineValue({ value, copyLabel }: { value: string; copyLabel: string }) {
  return (
    <button
      type="button"
      className="group flex min-w-0 items-start gap-1 rounded bg-muted px-2 py-1 text-left font-mono text-xs break-all hover:bg-muted/80"
      onClick={() => copyToClipboard(value, `${copyLabel} copied`)}
    >
      <span className="min-w-0 break-all">{value}</span>
      <CopyIcon className="mt-0.5 size-3 shrink-0 opacity-60 group-hover:opacity-100" />
    </button>
  );
}

function CopyableTextBlock({
  value,
  copyLabel,
  contentClassName,
}: {
  value: string;
  copyLabel: string;
  contentClassName?: string;
}) {
  return (
    <div className="group relative">
      <button
        type="button"
        className="absolute top-2 right-2 rounded bg-background/80 p-1 text-muted-foreground shadow-sm ring-1 ring-border hover:text-foreground"
        aria-label={`Copy ${copyLabel}`}
        title={`Copy ${copyLabel}`}
        onClick={() => copyToClipboard(value, `${copyLabel} copied`)}
      >
        <CopyIcon className="size-3.5" />
      </button>
      <pre
        className={cn(
          "max-h-96 overflow-auto rounded-md bg-muted p-3 pr-10 text-xs leading-5 whitespace-pre-wrap",
          contentClassName
        )}
      >
        {value}
      </pre>
    </div>
  );
}

function copyToClipboard(value: string, message: string) {
  void navigator.clipboard.writeText(value);
  toast.success(message);
}

function recordValue(value: unknown): JsonRecord | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }
  return value as JsonRecord;
}

function stringValue(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

function numberValue(value: unknown): number | null {
  return typeof value === "number" ? value : null;
}

function formatCreditMetric(value: number | null | undefined, unit?: "credits") {
  if (value == null) {
    return "N/A";
  }
  const formattedValue = creditMetricFormatter.format(value);
  if (!unit) {
    return formattedValue;
  }
  return `${formattedValue} ${value === 1 ? "credit" : "credits"}`;
}

function formatDate(value: number) {
  return new Date(value).toLocaleString("en-US", {
    dateStyle: "medium",
    timeStyle: "medium",
  });
}
